import { useCallback, useMemo, useState } from 'react';
import omit from 'lodash/omit';

import { useTranslation } from '@lyearn/i18n';
import * as ContentEditor from '@/components/ContentEditor';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { useAppConfig } from '@/hooks/useAppConfig';
import {
  getFormFields,
  getSectionLayout,
  validateForm,
} from '@/modules/corporate/modules/group/views/GroupEditor/components/Editor/config';
import useGroupAction from '@/modules/corporate/modules/group/views/GroupEditor/hooks/useGroupAction';
import { CreateUserGroupInput } from '@/modules/corporate/modules/group/views/GroupEditor/types';
import { getInitialFormValues } from '@/modules/corporate/modules/group/views/GroupEditor/utils';
import { GroupTypeEnum } from '@/types/schema';

const CreateGroupModal = ({
  onClose,
  onGroupCreate,
}: {
  onClose: () => void;
  onGroupCreate: (value: string) => void;
}) => {
  const { t } = useTranslation(['Assignment', 'Group']);
  const { site } = useAppConfig();
  const [formikSubmit, setFormikSubmit] = useState<any>();
  const [formValues, setFormValues] = useState();
  const { defaultContentVisibilitySettings, enableAssignmentRestriction } = site;
  const type = GroupTypeEnum.UserCollection;
  const { onSubmit } = useGroupAction({ type, onSubmit: onGroupCreate, onClose });

  const { formFields, sectionLayout } = useMemo(() => {
    const contentFormFields = getFormFields(t, type);

    return {
      formFields:
        type === GroupTypeEnum.UserCollection
          ? omit(contentFormFields, 'contentVisibilitySettings')
          : contentFormFields,
      sectionLayout: getSectionLayout(t, type, enableAssignmentRestriction),
    };
  }, [type, t, enableAssignmentRestriction]);

  const initialFormValues = useMemo(
    () =>
      getInitialFormValues({
        defaultContentVisibilitySettings,
        type,
        enableAssignmentRestriction,
      }),
    [defaultContentVisibilitySettings, enableAssignmentRestriction, type],
  );

  const _onChange = useCallback((formik: any) => {
    setFormValues(formik.values);
  }, []);

  return (
    <Modal
      action={{
        text: t('Assignment:CreateGroup.Create'),
        onSubmit: () => {
          formikSubmit?.();
        },
      }}
      header={{
        title: t('Assignment:CreateGroup.HeaderTitle'),
      }}
      variant={ModalVariant.ExtraWide}
      onClose={onClose}>
      <ContentEditor.Form<CreateUserGroupInput>
        description={t('Group:Editor.NewGroupDescription')}
        fields={formFields}
        formName="AddGroups"
        initialValues={initialFormValues}
        sectionLayout={sectionLayout}
        setSubmitRef={setFormikSubmit}
        title={t('Assignment:CreateGroup.FormTitle')}
        validate={validateForm}
        onChange={_onChange}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default CreateGroupModal;
